<template>
  <div ref="pdfRoot" class="learning-analytics-container" :class="{ 'export-mode': exportMode }">
    <div class="analytics-header">
      <div class="header-left">
        <i class="el-icon-arrow-left back-btn" @click="goBack"></i>
        <div>
          <h1 class="page-title">学情分析</h1>
          <div class="page-subtitle">基于做题记录、历年真题与学习进度形成当前科目画像</div>
        </div>
      </div>
      <el-button v-if="!exportMode" type="primary" size="small" icon="el-icon-refresh" @click="loadAll" :loading="loading">刷新分析</el-button>
    </div>

    <div v-if="loading" class="state-block">
      <i class="el-icon-loading"></i>
      <span>正在生成学情分析...</span>
    </div>

    <div v-else-if="error" class="state-block error-state">
      <i class="el-icon-warning-outline"></i>
      <span>{{ error }}</span>
    </div>

    <div v-else class="analytics-content">
      <div class="hero-card">
        <div class="hero-main">
          <div class="hero-title">{{ subjectLabel }} 学习概览</div>
          <div class="hero-summary">{{ overallSummary }}</div>
        </div>
      </div>

      <div class="metrics-grid">
        <div class="metric-card">
          <div class="metric-value">{{ totalRecords }}</div>
          <div class="metric-label">累计做题记录</div>
          <div class="metric-desc">当前科目下全部记录数</div>
        </div>
        <div class="metric-card">
          <div class="metric-value">{{ overallAccuracy }}%</div>
          <div class="metric-label">整体正确率</div>
          <div class="metric-desc">依据答题记录实时统计</div>
        </div>
        <div class="metric-card">
          <div class="metric-value">{{ recent7Accuracy }}%</div>
          <div class="metric-label">近7天表现</div>
          <div class="metric-desc">最近一周答题正确率</div>
        </div>
        <div class="metric-card">
          <div class="metric-value">{{ completedDays }}</div>
          <div class="metric-label">累计完成天数</div>
          <div class="metric-desc">按学习进度统计有效完成日</div>
        </div>
      </div>

      <div class="analysis-grid">
        <div class="panel-card full-span">
          <div class="panel-title">功能维度分析</div>
          <div class="source-grid">
            <div v-for="item in sourceInsights" :key="item.key" class="source-item">
              <div class="source-name">{{ item.label }}</div>
              <div class="source-meta">做了 {{ item.count }} 次</div>
            </div>
          </div>
        </div>

        <div class="panel-card full-span">
          <div class="panel-title">知识点错误分布</div>
          <div class="chart-grid">
            <div class="chart-card">
              <div ref="recentKnowledgeError" class="knowledge-chart"></div>
              <div class="chart-caption">展示近 7 日内答错题目所涉及知识点的占比</div>
            </div>
            <div class="chart-card">
              <div ref="allKnowledgeError" class="knowledge-chart"></div>
              <div class="chart-caption">展示当前科目全部错题中各知识点的错误分布</div>
            </div>
          </div>
        </div>

        <div class="panel-card full-span">
          <div class="panel-head">
            <div class="panel-title no-margin">AI 学习建议</div>
            <el-button type="primary" size="small" icon="el-icon-magic-stick" :loading="aiLoading" @click="generateAiAdvice">{{ aiAdvice ? '重新生成建议' : '生成学习建议' }}</el-button>
          </div>
          <div class="ai-tip">AI 会基于本页统计数据，给出阶段总结、薄弱点提醒和接下来 7 天的学习建议。</div>
          <div v-if="aiError" class="ai-error">{{ aiError }}</div>
          <div v-else-if="aiLoading" class="ai-loading">
            <div class="ai-loading-visual">
              <span class="ai-pulse-dot"></span>
              <span class="ai-pulse-dot"></span>
              <span class="ai-pulse-dot"></span>
            </div>
            <div class="ai-loading-text-wrap">
              <div class="ai-loading-title">AI 分析中{{ aiLoadingDots }}</div>
              <div class="ai-loading-desc">正在读取当前页面统计并生成学习建议，请稍候</div>
            </div>
          </div>
          <div v-else-if="aiAdvice" class="ai-content">
            <div v-if="aiGeneratedAt" class="ai-meta">生成时间：{{ aiGeneratedAt }}</div>
            <div class="ai-text">{{ aiAdvice }}</div>
          </div>
          <div v-else class="empty-text">点击上方按钮，可基于当前页面数据生成个性化学习建议。</div>
        </div>

        <div class="analytics-side-layout full-span">
          <div class="panel-card knowledge-panel">
            <div class="panel-title">知识点掌握情况</div>
            <div v-if="knowledgeInsights.length" class="knowledge-list knowledge-scroll-list">
              <div v-for="item in knowledgeInsights" :key="item.key" class="knowledge-item">
                <div class="knowledge-head">
                  <span class="knowledge-name">{{ item.name }}</span>
                  <span class="knowledge-rate" :class="item.wrong > 0 ? 'weak' : 'good'">错 {{ item.wrong }} 次</span>
                </div>
                <div class="knowledge-meta">练习 {{ item.total }} 次，做对 {{ item.correct }} 次，做错 {{ item.wrong }} 次</div>
              </div>
            </div>
            <div v-else class="empty-text">暂无可分析的知识点记录</div>
          </div>

          <div class="side-stack">
            <div class="panel-card compact-panel">
              <div class="panel-title">历年真题完成情况</div>
              <div class="paper-summary-row compact-paper-summary-row">
                <div class="paper-summary-box">
                  <div class="paper-summary-value">{{ completedPaperYears }}</div>
                  <div class="paper-summary-label">已完成套数</div>
                </div>
                <div class="paper-summary-box">
                  <div class="paper-summary-value">{{ totalPaperYears }}</div>
                  <div class="paper-summary-label">可练真题总套数</div>
                </div>
                <div class="paper-summary-box paper-summary-box-wide">
                  <div class="paper-summary-value">{{ paperCompletionRate }}%</div>
                  <div class="paper-summary-label">完成率</div>
                </div>
              </div>
              <div class="paper-tip">当前只保留真题完成汇总，不再展示每套真题的完成标签。</div>
            </div>

            <div class="panel-card compact-panel">
              <div class="panel-title">每日练习情况</div>
              <div class="daily-rows compact-daily-rows">
                <div class="daily-row">
                  <span>近7天活跃天数</span>
                  <strong>{{ activeDaysIn7 }}</strong>
                </div>
                <div class="daily-row">
                  <span>近30天活跃天数</span>
                  <strong>{{ activeDaysIn30 }}</strong>
                </div>
                <div class="daily-row">
                  <span>最近一次练习</span>
                  <strong>{{ lastPracticeDate || '暂无记录' }}</strong>
                </div>
              </div>
              <div class="daily-tip">{{ dailyInsight }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getRecordsByUserId } from '@/api/userQuestionRecord'
import { userStatusByUser } from '@/api/userStatus'
import { countCompletedDays, getProgressPaging } from '@/api/studyPlan'
import { getPastPaperYearStatus } from '@/api/pastPaperRecord'
import { textCompletion } from '@/api/ai'
import { listYearsByRepoId } from '@/api/courseQuestion'
import { batchGetGeneralKnowledgeNames } from '@/api/knowledge'
import { getTokenInfo } from '@/utils/jwtUtils'
import * as echarts from 'echarts'
import html2Canvas from 'html2canvas'
import JsPDF from 'jspdf'

export default {
  name: 'LearningAnalytics',
  props: {
    routeQueryOverride: {
      type: Object,
      default: () => null
    },
    preloadedData: {
      type: Object,
      default: () => null
    },
    exportMode: {
      type: Boolean,
      default: false
    },
    autoGenerateAi: {
      type: Boolean,
      default: false
    },
    autoExportPdf: {
      type: Boolean,
      default: false
    },
    pdfFileName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      currentUserId: null,
      records: [],
      statusList: [],
      paperYearStatus: {},
      availablePaperKeys: [],
      knowledgeNameMap: {},
      completedDays: 0,
      progressRows: [],
      aiLoading: false,
      aiAdvice: '',
      aiError: '',
      aiGeneratedAt: '',
      aiLoadingDots: '',
      aiLoadingTimer: null,
      chartInstances: {
        recentKnowledgeError: null,
        allKnowledgeError: null
      },
      exportRunning: false
    }
  },
  computed: {
    effectiveQuery() {
      return this.routeQueryOverride || this.$route?.query || {}
    },
    repoId() {
      const raw = this.effectiveQuery?.repoId
      const num = Number(raw)
      return Number.isFinite(num) && num > 0 ? num : null
    },
    subjectLabel() {
      if (this.preloadedData && this.preloadedData.subjectLabel) {
        return String(this.preloadedData.subjectLabel).trim()
      }
      return String(this.effectiveQuery?.level || this.effectiveQuery?.courseLevel || this.effectiveQuery?.courseTypeCode || '当前科目').trim()
    },
    totalRecords() {
      return this.records.length
    },
    correctRecords() {
      return this.records.filter(this.isCorrectRecord).length
    },
    overallAccuracy() {
      if (!this.totalRecords) return 0
      return Math.round((this.correctRecords / this.totalRecords) * 100)
    },
    recent7Records() {
      const end = Date.now()
      const start = end - 7 * 24 * 60 * 60 * 1000
      return this.records.filter(item => {
        const t = this.getRecordTime(item)
        if (!t) return false
        const ts = new Date(t).getTime()
        return !isNaN(ts) && ts >= start && ts <= end
      })
    },
    recent7Accuracy() {
      if (!this.recent7Records.length) return 0
      const correct = this.recent7Records.filter(this.isCorrectRecord).length
      return Math.round((correct / this.recent7Records.length) * 100)
    },
    wrongRecords() {
      return this.records.filter(item => !this.isCorrectRecord(item))
    },
    recent7WrongRecords() {
      return this.recent7Records.filter(item => !this.isCorrectRecord(item))
    },
    knowledgeInsights() {
      const bucket = {}
      this.records.forEach(item => {
        const ids = this.extractKnowledgeIds(item.knowledge)
        ids.forEach(id => {
          if (!bucket[id]) {
            bucket[id] = { total: 0, correct: 0 }
          }
          bucket[id].total += 1
          if (this.isCorrectRecord(item)) bucket[id].correct += 1
        })
      })
      return Object.keys(bucket)
        .map(id => {
          const total = bucket[id].total
          const correct = bucket[id].correct
          return {
            key: id,
            name: this.knowledgeNameMap[id] || `知识点 ${id}`,
            total,
            correct,
            wrong: total - correct,
            accuracy: total ? Math.round((correct / total) * 100) : 0
          }
        })
        .sort((a, b) => {
          if (a.accuracy !== b.accuracy) return a.accuracy - b.accuracy
          return b.total - a.total
        })
        .slice(0, 8)
    },
    recentKnowledgeErrorData() {
      return this.buildKnowledgeDistribution(this.recent7WrongRecords)
    },
    allKnowledgeErrorData() {
      return this.buildKnowledgeDistribution(this.wrongRecords)
    },
    sourceInsights() {
      const total = Array.isArray(this.statusList) ? this.statusList.length : 0
      if (!total) return []
      const grouped = {}
      this.statusList.forEach(item => {
        const key = this.normalizeSourceLabel(item?.source)
        grouped[key] = (grouped[key] || 0) + 1
      })
      return Object.keys(grouped)
        .map(key => {
          const count = grouped[key]
          const percent = Math.max(1, Math.round((count / total) * 100))
          return {
            key,
            label: key,
            count,
            percent,
            comment: this.describeSource(key, percent)
          }
        })
        .sort((a, b) => b.count - a.count)
    },
    paperYearRows() {
      const map = this.paperYearStatus || {}
      const keys = Array.isArray(this.availablePaperKeys) ? this.availablePaperKeys : []
      return keys
        .sort((a, b) => String(b).localeCompare(String(a)))
        .map(year => ({ year, done: !!map[year], detail: map[year] || null }))
    },
    totalPaperYears() {
      return this.paperYearRows.length
    },
    completedPaperYears() {
      return this.paperYearRows.filter(item => item.done).length
    },
    paperCompletionRate() {
      if (!this.totalPaperYears) return 0
      return Math.round((this.completedPaperYears / this.totalPaperYears) * 100)
    },
    activeDates() {
      const set = new Set()
      this.records.forEach(item => {
        const t = this.getRecordTime(item)
        if (!t) return
        const d = new Date(t)
        if (isNaN(d.getTime())) return
        set.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`)
      })
      return Array.from(set).sort()
    },
    activeDaysIn7() {
      return this.countRecentActiveDays(7)
    },
    activeDaysIn30() {
      return this.countRecentActiveDays(30)
    },
    lastPracticeDate() {
      return this.activeDates.length ? this.activeDates[this.activeDates.length - 1] : ''
    },
    dailyInsight() {
      if (!this.activeDates.length) return '当前科目下还没有形成连续练习数据，建议先完成每日练习。'
      if (this.activeDaysIn7 >= 5) return '近7天练习频率较高，学习节奏保持得不错。'
      if (this.activeDaysIn7 >= 3) return '近7天有稳定练习，建议继续提高连续性。'
      return '近7天练习次数偏少，建议结合每日计划保持稳定打卡。'
    },
    topWeakKnowledge() {
      return this.knowledgeInsights.slice(0, 5)
    },
    overallSummary() {
      if (!this.totalRecords) return '当前科目下暂无足够做题数据，建议先完成每日练习、知识点精练或历年真题后再查看分析。'
      const weakText = this.knowledgeInsights.length && this.knowledgeInsights[0].accuracy < 60
        ? `当前较薄弱的知识点是“${this.knowledgeInsights[0].name}”。`
        : '当前知识点表现整体较稳定。'
      const paperText = this.totalPaperYears
        ? `历年真题已完成 ${this.completedPaperYears}/${this.totalPaperYears} 套。`
        : '暂未检测到历年真题完成数据。'
      return `${weakText}${paperText} 详细数据见下方各分析卡片。`
    }
  },
  created() {
    if (this.preloadedData) {
      this.applyPreloadedData(this.preloadedData)
      if (this.autoExportPdf) {
        this.$nextTick(() => {
          this.renderKnowledgeCharts()
          this.runAutoExportFlow()
        })
      }
      return
    }
    this.loadAll()
  },
  beforeDestroy() {
    this.stopAiLoadingAnimation()
    this.disposeCharts()
  },
  methods: {
    goBack() {
      if (this.exportMode) return
      if (window.history.length > 1) {
        this.$router.go(-1)
        return
      }
      this.$router.push({
        path: '/amc-course',
        query: { ...(this.effectiveQuery || {}) }
      })
    },
    getRecordTime(row) {
      return row?.time || row?.createTime || row?.updatedAt || null
    },
    isCorrectRecord(row) {
      if (!row) return false
      if (typeof row.isRight === 'number') return row.isRight === 1
      if (typeof row.isRight === 'boolean') return row.isRight
      if (typeof row.isCorrect === 'number') return row.isCorrect === 1
      if (typeof row.isCorrect === 'boolean') return row.isCorrect
      return false
    },
    extractKnowledgeIds(value) {
      if (!value) return []
      return String(value)
        .split(/[，,;；\s]+/)
        .map(item => String(item).trim())
        .filter(item => /^\d+$/.test(item))
    },
    buildKnowledgeDistribution(records) {
      const bucket = {}
      ;(Array.isArray(records) ? records : []).forEach(item => {
        const ids = this.extractKnowledgeIds(item.knowledge)
        if (!ids.length) {
          bucket.UNKNOWN = (bucket.UNKNOWN || 0) + 1
          return
        }
        ids.forEach(id => {
          bucket[id] = (bucket[id] || 0) + 1
        })
      })
      return Object.keys(bucket)
        .map(key => ({
          key,
          name: key === 'UNKNOWN' ? '未标注知识点' : (this.knowledgeNameMap[key] || `知识点 ${key}`),
          value: bucket[key]
        }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 10)
    },
    countRecentActiveDays(days) {
      const end = Date.now()
      const start = end - days * 24 * 60 * 60 * 1000
      return this.activeDates.filter(item => {
        const ts = new Date(item).getTime()
        return !isNaN(ts) && ts >= start && ts <= end
      }).length
    },
    normalizeSourceLabel(value) {
      const raw = String(value || '未分类').trim() || '未分类'
      return raw
        .replace(/[（(]\d{4}-\d{2}-\d{2}[)）]\s*$/g, '')
        .replace(/[（(]\d{4}\/\d{2}\/\d{2}[)）]\s*$/g, '')
        .trim() || '未分类'
    },
    describeSource(label, percent) {
      if (percent >= 40) return `${label} 是你当前最主要的学习场景。`
      if (percent >= 20) return `${label} 有较稳定的使用频率。`
      return `${label} 已有一定学习记录，可继续加强。`
    },
    formatDateTime(date) {
      const d = date instanceof Date ? date : new Date(date)
      if (isNaN(d.getTime())) return ''
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    },
    startAiLoadingAnimation() {
      this.stopAiLoadingAnimation()
      this.aiLoadingDots = ''
      this.aiLoadingTimer = setInterval(() => {
        this.aiLoadingDots = this.aiLoadingDots.length >= 3 ? '' : `${this.aiLoadingDots}.`
      }, 450)
    },
    stopAiLoadingAnimation() {
      if (this.aiLoadingTimer) {
        clearInterval(this.aiLoadingTimer)
        this.aiLoadingTimer = null
      }
      this.aiLoadingDots = ''
    },
    buildAiPrompt() {
      const sourceText = this.sourceInsights.length
        ? this.sourceInsights.map(item => `${item.label}：${item.count} 次，占比 ${item.percent}%`).join('；')
        : '暂无功能使用记录'
      const weakKnowledgeText = this.topWeakKnowledge.length
        ? this.topWeakKnowledge.map(item => `${item.name}：正确率 ${item.accuracy}%，练习 ${item.total} 次`).join('；')
        : '暂无知识点掌握数据'
      const recentWrongText = this.recentKnowledgeErrorData.length
        ? this.recentKnowledgeErrorData.slice(0, 5).map(item => `${item.name}：${item.value} 次错误`).join('；')
        : '近7日暂无错题'
      const allWrongText = this.allKnowledgeErrorData.length
        ? this.allKnowledgeErrorData.slice(0, 5).map(item => `${item.name}：${item.value} 次错误`).join('；')
        : '暂无历史错题'
      return [
        `你是一名资深学习教练，请基于以下学情分析数据，为学生提供简明、具体、可执行的学习建议。`,
        `请用中文回答，并严格按以下四个部分输出：`,
        `1. 当前学习状态总结`,
        `2. 主要问题定位`,
        `3. 接下来7天学习建议`,
        `4. 需要优先突破的知识点或功能模块`,
        `每个部分控制在2到4句，避免空话。`,
        `科目：${this.subjectLabel}`,
        `累计做题记录：${this.totalRecords}`,
        `整体正确率：${this.overallAccuracy}%`,
        `近7天正确率：${this.recent7Accuracy}%`,
        `累计完成天数：${this.completedDays}`,
        `近7天活跃天数：${this.activeDaysIn7}`,
        `近30天活跃天数：${this.activeDaysIn30}`,
        `最近一次练习：${this.lastPracticeDate || '暂无记录'}`,
        `历年真题完成：${this.completedPaperYears}/${this.totalPaperYears} 套，完成率 ${this.paperCompletionRate}%`,
        `功能使用分布：${sourceText}`,
        `薄弱知识点：${weakKnowledgeText}`,
        `近7日错误知识点分布：${recentWrongText}`,
        `全部错误知识点分布：${allWrongText}`,
        `页面总结：${this.overallSummary}`,
        `请直接输出建议正文，不要使用 markdown 代码块。`
      ].join('\n')
    },
    async generateAiAdvice() {
      if (!this.totalRecords) {
        this.$message.warning('当前做题数据不足，暂时无法生成 AI 建议')
        return
      }
      this.aiLoading = true
      this.aiError = ''
      this.startAiLoadingAnimation()
      try {
        const prompt = this.buildAiPrompt()
        const resp = await textCompletion(prompt)
        const content = resp?.data ?? resp?.result?.data ?? resp?.result ?? ''
        const text = String(content || '').trim()
        if (!text) {
          throw new Error(resp?.msg || 'AI 未返回有效建议内容')
        }
        this.aiAdvice = text
        this.aiError = ''
        this.aiGeneratedAt = this.formatDateTime(new Date())
      } catch (e) {
        console.error('生成AI学习建议失败:', e)
        this.aiError = e?.message || '生成 AI 学习建议失败，请稍后重试'
      } finally {
        this.aiLoading = false
        this.stopAiLoadingAnimation()
      }
    },
    async exportCurrentViewToPdf() {
      const root = this.$refs.pdfRoot
      if (!root) {
        throw new Error('未找到导出区域')
      }
      const canvas = await html2Canvas(root, {
        allowTaint: true,
        dpi: window.devicePixelRatio * 2,
        scale: 2,
        useCORS: true,
        backgroundColor: '#f5f7fb'
      })
      const contentWidth = canvas.width
      const contentHeight = canvas.height
      const pdfWidth = 595.28
      const pdfHeight = pdfWidth / contentWidth * contentHeight
      const pageData = canvas.toDataURL('image/jpeg', 1.0)
      const pdf = new JsPDF('p', 'pt', [pdfWidth, pdfHeight])
      pdf.addImage(pageData, 'JPEG', 0, 0, pdfWidth, pdfHeight)
      const filename = (this.pdfFileName || `${this.subjectLabel || '学情分析'}-学情分析报告`).replace(/[\\/:*?"<>|]/g, '-')
      pdf.save(`${filename}.pdf`)
    },
    applyPreloadedData(payload) {
      const data = payload || {}
      this.records = Array.isArray(data.records) ? data.records : []
      this.statusList = Array.isArray(data.statusList) ? data.statusList : []
      this.paperYearStatus = data.paperYearStatus || {}
      this.availablePaperKeys = Array.isArray(data.availablePaperKeys) ? data.availablePaperKeys : []
      this.knowledgeNameMap = data.knowledgeNameMap || {}
      this.completedDays = Number(data.completedDays || 0) || 0
      this.progressRows = Array.isArray(data.progressRows) ? data.progressRows : []
      this.aiAdvice = String(data.aiAdvice || '').trim()
      this.aiGeneratedAt = String(data.aiGeneratedAt || '').trim()
      this.aiError = ''
      this.error = null
      this.loading = false
    },
    async runAutoExportFlow() {
      if (this.exportRunning) return
      this.exportRunning = true
      try {
        if (this.autoGenerateAi && !this.aiAdvice) {
          await this.generateAiAdvice()
          if (this.aiError) {
            throw new Error(this.aiError)
          }
        }
        await this.$nextTick()
        await new Promise(resolve => setTimeout(resolve, 500))
        this.renderKnowledgeCharts()
        await new Promise(resolve => setTimeout(resolve, 500))
        await this.exportCurrentViewToPdf()
        this.$emit('export-success')
      } catch (e) {
        this.$emit('export-error', e)
      } finally {
        this.exportRunning = false
      }
    },
    async loadKnowledgeNames() {
      const ids = Array.from(new Set(this.records.flatMap(item => this.extractKnowledgeIds(item.knowledge))))
      if (!ids.length) {
        this.knowledgeNameMap = {}
        return
      }
      try {
        const resp = await batchGetGeneralKnowledgeNames(ids)
        const data = resp?.data?.data ?? resp?.data ?? resp
        const map = {}
        if (Array.isArray(data)) {
          data.forEach(item => {
            if (item && item.id != null) map[item.id] = item.content || item.name || item.title || `知识点 ${item.id}`
          })
        } else if (data && typeof data === 'object') {
          Object.assign(map, data)
        }
        this.knowledgeNameMap = map
      } catch (e) {
        this.knowledgeNameMap = {}
      }
    },
    initPieChart(refName, title, data) {
      const chartDom = this.$refs[refName]
      if (!chartDom) return
      const existing = this.chartInstances[refName]
      const chart = existing || echarts.init(chartDom)
      this.chartInstances[refName] = chart
      chart.setOption({
        tooltip: { trigger: 'item' },
        legend: { bottom: 0, left: 'center', type: 'scroll' },
        title: {
          text: title,
          left: 'center',
          top: 0,
          textStyle: { fontSize: 15, fontWeight: 600, color: '#303133' }
        },
        series: [{
          type: 'pie',
          radius: ['35%', '68%'],
          center: ['50%', '52%'],
          avoidLabelOverlap: true,
          itemStyle: { borderRadius: 8, borderColor: '#fff', borderWidth: 2 },
          label: {
            formatter: '{b}\n{d}%'
          },
          data: data.length ? data : [{ name: '暂无错误记录', value: 1, itemStyle: { color: '#dcdfe6' } }]
        }]
      })
    },
    renderKnowledgeCharts() {
      this.$nextTick(() => {
        this.initPieChart('recentKnowledgeError', '近7日知识点错误分布', this.recentKnowledgeErrorData)
        this.initPieChart('allKnowledgeError', '全部知识点错误分布', this.allKnowledgeErrorData)
      })
    },
    disposeCharts() {
      Object.keys(this.chartInstances).forEach(key => {
        const chart = this.chartInstances[key]
        if (chart) {
          chart.dispose()
          this.chartInstances[key] = null
        }
      })
    },
    async loadAll() {
      this.loading = true
      this.error = null
      try {
        const userInfo = getTokenInfo && getTokenInfo()
        this.currentUserId = userInfo ? (userInfo.id ?? userInfo.userId) : null
        if (!this.currentUserId) {
          this.error = '未获取到用户信息，请重新登录后再试'
          return
        }
        if (!this.repoId) {
          this.error = '未识别当前科目，无法生成学情分析'
          return
        }
        const now = new Date()
        const start30 = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29)
        const start30Str = `${start30.getFullYear()}-${String(start30.getMonth() + 1).padStart(2, '0')}-${String(start30.getDate()).padStart(2, '0')}`
        const endStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
        const courseType = String(this.effectiveQuery?.courseType || this.effectiveQuery?.courseTypeCode || '')

        const [recordsResp, statusResp, paperResp, completedDaysResp, progressResp, paperKeysResp] = await Promise.all([
          getRecordsByUserId(this.currentUserId, this.repoId),
          userStatusByUser(this.currentUserId, this.repoId),
          getPastPaperYearStatus(this.currentUserId, this.repoId),
          countCompletedDays(this.currentUserId, this.repoId, undefined, undefined),
          getProgressPaging({ pageNum: 1, pageSize: 60, userId: this.currentUserId, repoId: this.repoId, dateFrom: start30Str, dateTo: endStr }),
          listYearsByRepoId(this.repoId, courseType)
        ])

        this.records = Array.isArray(recordsResp?.data) ? recordsResp.data : (Array.isArray(recordsResp?.data?.data) ? recordsResp.data.data : [])
        this.statusList = Array.isArray(statusResp?.data) ? statusResp.data : (Array.isArray(statusResp?.data?.data) ? statusResp.data.data : [])
        this.paperYearStatus = paperResp?.data?.data ?? paperResp?.data ?? {}
        const paperKeysPayload = paperKeysResp?.data?.data ?? paperKeysResp?.data ?? {}
        if (paperKeysPayload && typeof paperKeysPayload === 'object' && !Array.isArray(paperKeysPayload)) {
          this.availablePaperKeys = Object.keys(paperKeysPayload)
        } else {
          const list = Array.isArray(paperKeysPayload) ? paperKeysPayload : (Array.isArray(paperKeysPayload?.list) ? paperKeysPayload.list : [])
          this.availablePaperKeys = list.map(String)
        }
        const completedPayload = completedDaysResp?.data?.data ?? completedDaysResp?.data ?? {}
        this.completedDays = Number(completedPayload.completedDays ?? completedPayload.days ?? completedPayload.count ?? 0) || 0
        const progressPayload = progressResp?.data?.records ?? progressResp?.data?.data?.records ?? progressResp?.data?.data ?? progressResp?.data ?? []
        this.progressRows = Array.isArray(progressPayload) ? progressPayload : []

        await this.loadKnowledgeNames()
      } catch (e) {
        console.error('加载学情分析失败:', e)
        this.error = '加载学情分析失败，请稍后重试'
      } finally {
        this.loading = false
        if (!this.error) {
          this.$nextTick(() => {
            this.renderKnowledgeCharts()
          })
          if (this.autoExportPdf) {
            this.$nextTick(() => {
              this.runAutoExportFlow()
            })
          }
        }
      }
    }
  }
}
</script>

<style scoped>
.learning-analytics-container {
  min-height: 100vh;
  padding: 24px;
  background: #f5f7fb;
}
.analytics-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}
.header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}
.back-btn {
  font-size: 22px;
  cursor: pointer;
  color: #409eff;
}
.page-title {
  margin: 0;
  font-size: 28px;
  color: #1f2d3d;
}
.page-subtitle {
  margin-top: 6px;
  color: #8492a6;
  font-size: 14px;
}
.state-block {
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #606266;
  background: #fff;
  border-radius: 16px;
}
.error-state {
  color: #f56c6c;
}
.analytics-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.hero-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 18px;
  padding: 24px;
  color: #fff;
  display: flex;
  justify-content: space-between;
  gap: 16px;
}
.hero-title {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 10px;
}
.hero-summary {
  line-height: 1.7;
  opacity: 0.95;
}
.hero-tags {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-end;
}
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}
.metric-card,
.panel-card {
  background: #fff;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 8px 24px rgba(31, 45, 61, 0.06);
}
.metric-value {
  font-size: 28px;
  font-weight: 700;
  color: #303133;
}
.metric-label {
  margin-top: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}
.metric-desc {
  margin-top: 6px;
  color: #909399;
  font-size: 13px;
}
.analysis-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}
.analytics-side-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 0.95fr;
  gap: 16px;
  align-items: stretch;
}
.knowledge-panel {
  min-height: 100%;
}
.side-stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.compact-panel {
  min-height: 0;
}
.full-span {
  grid-column: 1 / -1;
}
.panel-title {
  font-size: 18px;
  font-weight: 700;
  color: #303133;
  margin-bottom: 16px;
}
.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
.no-margin {
  margin-bottom: 0;
}
.source-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}
.chart-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}
.chart-card {
  background: #f8faff;
  border-radius: 14px;
  padding: 16px;
}
.knowledge-chart {
  width: 100%;
  height: 360px;
}
.chart-caption {
  margin-top: 8px;
  color: #909399;
  font-size: 13px;
  text-align: center;
}
.ai-tip,
.ai-meta,
.ai-error {
  font-size: 13px;
}
.ai-tip {
  color: #909399;
  margin-bottom: 12px;
}
.ai-loading {
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
  color: #606266;
  background: #f8faff;
  border-radius: 12px;
}
.ai-loading-visual {
  display: flex;
  align-items: center;
  gap: 8px;
}
.ai-pulse-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: linear-gradient(135deg, #409eff 0%, #7c4dff 100%);
  animation: aiPulse 1.2s infinite ease-in-out;
}
.ai-pulse-dot:nth-child(2) {
  animation-delay: 0.2s;
}
.ai-pulse-dot:nth-child(3) {
  animation-delay: 0.4s;
}
.ai-loading-text-wrap {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.ai-loading-title {
  color: #303133;
  font-size: 16px;
  font-weight: 600;
}
.ai-loading-desc {
  color: #909399;
  font-size: 13px;
}
.ai-content {
  background: linear-gradient(180deg, #f8faff 0%, #ffffff 100%);
  border: 1px solid #ebeef5;
  border-radius: 14px;
  padding: 16px;
}
.ai-meta {
  color: #909399;
  margin-bottom: 10px;
}
.ai-text {
  white-space: pre-wrap;
  line-height: 1.9;
  color: #303133;
  font-size: 14px;
}
.ai-error {
  color: #f56c6c;
  background: #fef0f0;
  border-radius: 12px;
  padding: 12px 14px;
}
@keyframes aiPulse {
  0%,
  80%,
  100% {
    transform: scale(0.7);
    opacity: 0.45;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}
.source-item,
.knowledge-item {
  padding: 14px;
  background: #f8faff;
  border-radius: 12px;
}
.source-item {
  min-height: 88px;
}
.source-name,
.knowledge-name {
  font-weight: 600;
  color: #303133;
}
.source-meta,
.knowledge-meta,
.empty-text,
.daily-tip {
  color: #909399;
  font-size: 13px;
}
.source-meta,
.knowledge-meta {
  margin: 8px 0;
}
.knowledge-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.knowledge-scroll-list {
  max-height: 620px;
  overflow-y: auto;
  padding-right: 6px;
}
.knowledge-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.knowledge-rate.good {
  color: #67c23a;
}
.knowledge-rate.mid {
  color: #e6a23c;
}
.knowledge-rate.weak {
  color: #f56c6c;
}
.paper-summary-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}
.compact-paper-summary-row {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.paper-summary-box {
  background: #f8faff;
  border-radius: 12px;
  padding: 14px;
  text-align: center;
}
.paper-summary-box-wide {
  grid-column: 1 / -1;
}
.paper-summary-value {
  font-size: 24px;
  font-weight: 700;
  color: #303133;
}
.paper-summary-label {
  margin-top: 8px;
  font-size: 13px;
  color: #909399;
}
.paper-tip {
  color: #909399;
  font-size: 13px;
}
.daily-rows {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 12px;
}
.compact-daily-rows {
  margin-bottom: 10px;
}
.daily-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  background: #f8faff;
  border-radius: 12px;
  color: #303133;
}
@media (max-width: 1200px) {
  .metrics-grid,
  .analysis-grid,
  .analytics-side-layout,
  .source-grid,
  .chart-grid,
  .paper-summary-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .analytics-side-layout {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 768px) {
  .learning-analytics-container {
    padding: 16px;
  }
  .analytics-header,
  .hero-card,
  .panel-head {
    flex-direction: column;
    align-items: flex-start;
  }
  .metrics-grid,
  .analysis-grid,
  .analytics-side-layout,
  .source-grid,
  .chart-grid,
  .paper-summary-row {
    grid-template-columns: 1fr;
  }
  .hero-tags {
    align-items: flex-start;
  }
}
</style>
